import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import PageHero from '../components/ui/PageHero';
import './LegalPage.css';

export default function PrivacyPolicy() {
  return (
    <>
      <Helmet>
        <title>Privatlivspolitik – TopCasinoDansk</title>
        <meta name="description" content="Læs TopCasinoDansks privatlivspolitik og forstå, hvordan vi indsamler, bruger og beskytter dine personoplysninger." />
      </Helmet>

      <PageHero
        title="Privatlivspolitik"
        subtitle="Sidst opdateret: april 2026"
        breadcrumb={<><Link to="/">Forside</Link> › Privatlivspolitik</>}
      />

      <section className="legal-section section">
        <div className="container legal-layout">
          <nav className="legal-toc">
            <div className="legal-toc-inner">
              <h4>Indhold</h4>
              <ol>
                <li><a href="#dataansvarlig">1. Dataansvarlig</a></li>
                <li><a href="#hvad-vi-indsamler">2. Hvad vi indsamler</a></li>
                <li><a href="#formal">3. Formål og retsgrundlag</a></li>
                <li><a href="#tredjepart">4. Tredjepartstjenester</a></li>
                <li><a href="#dine-rettigheder">5. Dine rettigheder</a></li>
                <li><a href="#opbevaring">6. Opbevaringsperiode</a></li>
                <li><a href="#cookies">7. Cookies</a></li>
                <li><a href="#sikkerhed">8. Sikkerhed</a></li>
                <li><a href="#kontakt">9. Kontakt</a></li>
              </ol>
            </div>
          </nav>

          <article className="legal-content">
            <div className="legal-intro">
              <p>
                TopCasinoDansk.com ("vi", "os", "vores") er en uafhængig dansk guide og sammenligningsportal for online casino.
                Denne privatlivspolitik beskriver, hvordan vi indsamler, anvender og beskytter dine personoplysninger i overensstemmelse
                med EU's databeskyttelsesforordning (GDPR) og dansk lovgivning.
              </p>
            </div>

            <section id="dataansvarlig">
              <h2>1. Dataansvarlig</h2>
              <p>Den dataansvarlige for behandling af personoplysninger på dette websted er:</p>
              <div className="legal-info-box">
                <p><strong>TopCasinoDansk.com</strong><br />
                Uafhængig redaktion<br />
                E-mail: privacy@topcasinodansk.com</p>
              </div>
            </section>

            <section id="hvad-vi-indsamler">
              <h2>2. Hvad vi indsamler</h2>
              <p>Vi kan indsamle følgende typer af personoplysninger:</p>

              <h3>2.1 Oplysninger du giver os</h3>
              <ul className="legal-list">
                <li>Navn og e-mailadresse, når du udfylder vores kontaktformular</li>
                <li>Indholdet af dine beskeder til os</li>
                <li>Eventuelle andre oplysninger, du frivilligt deler med os</li>
              </ul>

              <h3>2.2 Automatisk indsamlede oplysninger</h3>
              <ul className="legal-list">
                <li>IP-adresse (anonymiseret ved hjælp af Google Analytics)</li>
                <li>Browsertype og -version</li>
                <li>Operativsystem</li>
                <li>Besøgte sider og klikstrøm</li>
                <li>Tidspunkt og varighed af dit besøg</li>
                <li>Henvisende websted (referrer)</li>
              </ul>
            </section>

            <section id="formal">
              <h2>3. Formål og retsgrundlag</h2>
              <div className="legal-table-wrap">
                <table className="legal-table">
                  <thead>
                    <tr>
                      <th>Formål</th>
                      <th>Retsgrundlag</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Besvarelse af henvendelser via kontaktformular</td>
                      <td>Legitim interesse (GDPR art. 6(1)(f))</td>
                    </tr>
                    <tr>
                      <td>Statistik og forbedring af webstedets ydeevne</td>
                      <td>Samtykke (GDPR art. 6(1)(a))</td>
                    </tr>
                    <tr>
                      <td>Affiliate-sporing og kommissionsberegning</td>
                      <td>Samtykke (GDPR art. 6(1)(a))</td>
                    </tr>
                    <tr>
                      <td>Opfyldelse af lovmæssige forpligtelser</td>
                      <td>Retlig forpligtelse (GDPR art. 6(1)(c))</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section id="tredjepart">
              <h2>4. Tredjepartstjenester</h2>
              <p>Vi anvender følgende tredjepartstjenester, som kan behandle data om dig:</p>

              <h3>4.1 Google Analytics</h3>
              <p>
                Vi bruger Google Analytics til at analysere trafik og brugeradfærd på vores websted.
                Google Analytics anvender cookies og kan videresende data til Google-servere i USA.
                Vi har konfigureret anonymisering af IP-adresser. Du kan fravælge Google Analytics via{' '}
                <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
                  Google Analytics Opt-out Browser Add-on
                </a>.
              </p>

              <h3>4.2 Affiliate-netværk</h3>
              <p>
                Vores websted indeholder affiliate-links til online casinoer. Når du klikker på disse links,
                kan affiliate-netværker sætte trackingcookies på din enhed for at registrere, om du efterfølgende
                opretter en konto. Vi kontrollerer ikke disse tredjeparters databehandling og anbefaler, at du
                læser deres privatlivspolitikker.
              </p>
            </section>

            <section id="dine-rettigheder">
              <h2>5. Dine rettigheder</h2>
              <p>I henhold til GDPR har du følgende rettigheder:</p>
              <ul className="legal-list">
                <li><strong>Ret til indsigt:</strong> Du har ret til at få indsigt i de personoplysninger, vi behandler om dig.</li>
                <li><strong>Ret til berigtigelse:</strong> Du kan kræve, at urigtige eller ufuldstændige oplysninger berigtiges.</li>
                <li><strong>Ret til sletning:</strong> Under visse omstændigheder kan du kræve dine oplysninger slettet ("retten til at blive glemt").</li>
                <li><strong>Ret til begrænsning:</strong> Du kan anmode om begrænsning af behandlingen af dine oplysninger.</li>
                <li><strong>Ret til dataportabilitet:</strong> Du kan modtage dine oplysninger i et struktureret, maskinlæsbart format.</li>
                <li><strong>Ret til indsigelse:</strong> Du kan gøre indsigelse mod behandling baseret på legitim interesse.</li>
                <li><strong>Ret til at tilbagekalde samtykke:</strong> Samtykke kan til enhver tid tilbagekaldes uden at påvirke lovligheden af den forudgående behandling.</li>
              </ul>
              <p>
                For at udøve dine rettigheder, kontakt os på privacy@topcasinodansk.com.
                Du kan også klage til Datatilsynet via <a href="https://www.datatilsynet.dk/" target="_blank" rel="noopener noreferrer">datatilsynet.dk</a>.
              </p>
            </section>

            <section id="opbevaring">
              <h2>6. Opbevaringsperiode</h2>
              <p>
                Vi opbevarer dine personoplysninger, så længe det er nødvendigt for at opfylde de formål, hvortil
                de er indsamlet, medmindre en længere opbevaringsperiode er påkrævet eller tilladt ved lov.
              </p>
              <ul className="legal-list">
                <li>Kontakthenvendelser: Op til 2 år efter svargivning</li>
                <li>Statistikdata: Op til 26 måneder (Google Analytics standard)</li>
                <li>Cookies: Se vores cookiepolitik for detaljer</li>
              </ul>
            </section>

            <section id="cookies">
              <h2>7. Cookies</h2>
              <p>
                Vi anvender cookies på vores websted. For en detaljeret oversigt over vores cookiebrug, se vores{' '}
                <Link to="/cookiepolitik">cookiepolitik</Link>.
              </p>
            </section>

            <section id="sikkerhed">
              <h2>8. Sikkerhed</h2>
              <p>
                Vi træffer rimelige tekniske og organisatoriske foranstaltninger for at beskytte dine personoplysninger
                mod utilsigtet eller ulovlig destruktion, tab, ændring, uautoriseret videregivelse eller adgang.
                Vores websted anvender HTTPS til at kryptere al datatransmission.
              </p>
            </section>

            <section id="kontakt">
              <h2>9. Kontakt og klager</h2>
              <p>
                Har du spørgsmål til vores behandling af dine personoplysninger, er du velkommen til at kontakte os:
              </p>
              <div className="legal-info-box">
                <p>
                  <strong>E-mail:</strong> privacy@topcasinodansk.com<br />
                  <strong>Datatilsynet:</strong> <a href="https://www.datatilsynet.dk/" target="_blank" rel="noopener noreferrer">datatilsynet.dk</a>
                </p>
              </div>
              <p>
                Denne privatlivspolitik kan opdateres løbende. Eventuelle væsentlige ændringer vil blive meddelt
                på vores websted. Politikken er sidst opdateret i april 2026.
              </p>
            </section>
          </article>
        </div>
      </section>
    </>
  );
}
